import { CODE_SYSTEMS, LOINC_CODES } from '@liza/fhir-utils';
import {
  activeConditionsQuery,
  activeMedicationRequestsQuery,
  earliestAuthoredOn,
  evidenceQueriesFor,
  latestObservation,
  medicationRequestsIn,
  type ContextResources,
} from '../planner/context';
import type { ProtocolSpec } from '../planner/plan';
import type { ProtocolVariables } from '../planner/types';
import { aceInhibitorsValueSet, angiotensinReceptorBlockersValueSet } from '../valueSets/rxNormDrugClasses';
import { hypertensionMonitoring } from './hypertensionMonitoring';

/**
 * Binds exactly the variables `HYPERTENSION_CONTEXT_VARIABLES` declares (a test pins the two lists to
 * each other).
 *
 * "RASi" is one clinical idea across two ValueSets, because ATC separates ACE inhibitors from ARBs
 * and the guideline does not. The union is bound as a single variable so a condition reads the idea
 * rather than the taxonomy.
 */
export const deriveHypertensionVariables = (resources: ContextResources): ProtocolVariables => {
  const rasi = [
    ...medicationRequestsIn(resources, aceInhibitorsValueSet),
    ...medicationRequestsIn(resources, angiotensinReceptorBlockersValueSet),
  ];
  return {
    '%onRasi': { kind: 'boolean', value: rasi.length > 0 },
    '%rasiStartedAt': { kind: 'dateTime', value: earliestAuthoredOn(rasi) },
    // The post-initiation checks compare their own latest result against the prescription date, so
    // each needs the Observation rather than a boolean.
    '%potassium': { kind: 'resource', value: latestObservation(resources, CODE_SYSTEMS.LOINC, LOINC_CODES.POTASSIUM) },
    '%creatinine': {
      kind: 'resource',
      value: latestObservation(resources, CODE_SYSTEMS.LOINC, LOINC_CODES.CREATININE),
    },
  };
};

/** The hypertension protocol, ready for `planProtocol`. */
export const hypertensionMonitoringProtocol: ProtocolSpec = {
  planDefinition: hypertensionMonitoring,
  valueSets: [aceInhibitorsValueSet, angiotensinReceptorBlockersValueSet],
  queries: [activeConditionsQuery, activeMedicationRequestsQuery, ...evidenceQueriesFor(hypertensionMonitoring)],
  deriveVariables: deriveHypertensionVariables,
};
